import { Jsonified } from "@redotech/json/json";
import * as zExt from "@redotech/rpc/ext";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { NullishPartial } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { Address } from "./address";
import { MarketingEntryPoint } from "./advanced-flow/schemas/marketing/marketing";
import { customerCustomFieldInstanceSchema } from "./customer-custom-fields/customer-custom-fields-definition";
import { CustomerTagSource } from "./customer-tags/customer-tag-record";
import {
  LoyaltyEarningRuleType,
  LoyaltySocialMediaInteractionTypeSchema,
} from "./loyalty/loyalty-definition";
import { YofiScoreSchema } from "./order-schema";
import { PillTheme } from "./pill-theme";
import { Address as ReturnAddress } from "./return-flow";

export const REDO_CUSTOMER_TAG = "redo-customer";

export type CustomerJson = Jsonified<Customer>;

// TODO: Migrate this to match the address schema from "@redotech/redo-model/address
type CustomerAddress = Omit<ReturnAddress, "email"> & {
  country_name?: string; // the DB model was out of sync with this model for a while. Keeping this for backwards compatibility.
};

export interface CustomerCurrentLocation extends Partial<Address> {
  countryCode?: string;
  stateCode?: string;
  latitude?: number;
  longitude?: number;
  geoLocation?: { type: "Point"; coordinates: [number, number] };
  ianaTimeZoneName?: string;
  source: LocationSource;
  updatedAt: Date;
  updatedByEventTimestamp?: Date | null;
}

export enum LocationSource {
  WARRANTY_REGISTRATION = "warranty_registration",
  BILLING_ADDRESS = "billing_address",
  IP_ADDRESS = "ip_address",
}

export const locationSourcePriorityMap: Record<LocationSource, number> = {
  [LocationSource.WARRANTY_REGISTRATION]: 1,
  [LocationSource.BILLING_ADDRESS]: 1,
  [LocationSource.IP_ADDRESS]: 2,
};

/**
 * Deterministic comparator for customer locations.
 * Returns negative when a is preferred over b, positive when b is preferred, 0 when equal.
 * Preference rules:
 * 1) Higher priority source first (lower numeric value in locationSourcePriorityMap)
 * 2) Newer timestamp first (updatedByEventTimestamp if present, otherwise updatedAt)
 */
export function compareCustomerCurrentLocation(
  a: CustomerCurrentLocation,
  b: CustomerCurrentLocation,
): number {
  const prioA = locationSourcePriorityMap[a.source];
  const prioB = locationSourcePriorityMap[b.source];
  if (prioA !== prioB) return prioA - prioB; // lower number wins

  const timeA = (a.updatedByEventTimestamp ?? a.updatedAt).getTime();
  const timeB = (b.updatedByEventTimestamp ?? b.updatedAt).getTime();
  // newer first (desc by time)
  return timeB - timeA;
}

export function shouldUpdateCustomerCurrentLocation({
  oldLocation,
  newLocation,
}: {
  oldLocation?: CustomerCurrentLocation | null;
  newLocation: CustomerCurrentLocation;
}) {
  if (!oldLocation) return true;
  // Prefer new over old if comparator says new is better than old
  return compareCustomerCurrentLocation(newLocation, oldLocation) < 0;
}

export enum CustomerOrigin {
  EMAIL = "email",
  REDO_CHAT = "redoChat",
  SHOPIFY = "shopify",
  INSTAGRAM = "instagram",
  FACEBOOK = "facebook",
  INSTAGRAM_COMMENTS = "instagramComments",
  FACEBOOK_COMMENTS = "facebookComments",
  REDO = "redo",
  ATTENTIVE = "attentive",
  POSTSCRIPT = "postscript",
  WARRANTY = "warranty",
  KLAVIYO = "klaviyo",
  TIKTOK = "tiktok",
  IMPORT = "import",
  UNKNOWN = "unknown",
}

export enum MarketingConsentStatus {
  NOT_SUBSCRIBED = "not_subscribed",
  SUBSCRIBED = "subscribed",
  CONFIRMED = "confirmed",
  UNSUBSCRIBED = "unsubscribed",
}

const consentPriority: Record<MarketingConsentStatus, number> = {
  [MarketingConsentStatus.UNSUBSCRIBED]: 3,
  [MarketingConsentStatus.CONFIRMED]: 2,
  [MarketingConsentStatus.SUBSCRIBED]: 1,
  [MarketingConsentStatus.NOT_SUBSCRIBED]: 0,
};

export function getHighestMarketingConsentStatus(
  a: MarketingConsentStatus,
  b: MarketingConsentStatus,
) {
  return consentPriority[a] >= consentPriority[b] ? a : b;
}

interface BaseCustomerInfo {
  id: string;
}
export interface ShopifyCustomerInfo extends BaseCustomerInfo {}
export interface CommentsoldCustomerInfo extends BaseCustomerInfo {}

export interface InstagramInfo {
  igScopedUserId: string;
  username?: string | null;
  name?: string | null;
  profilePic?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

export interface FacebookInfo {
  pageScopedId: string;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profilePic?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

export interface TiktokInfo {
  userId: string;
  name?: string | null;
  profilePic?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

export interface PostscriptInfo {
  subscriberId: string;
}

export interface AnonymousIdInfo {
  id: string;
}

export interface UpdateCustomerInfoData {
  _id: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
}

export function marketingSubscriptionsToMarketingSubscriptionStatuses(
  subscriptions: CustomerMarketingSubscriptions,
): CustomerMarketingSubscriptionStatuses {
  return {
    email: subscriptions.email
      ? baseMarketingSubscriptionToConsentStatus(subscriptions.email)
      : MarketingConsentStatus.NOT_SUBSCRIBED,
    sms: subscriptions.sms
      ? baseMarketingSubscriptionToConsentStatus(subscriptions.sms)
      : MarketingConsentStatus.NOT_SUBSCRIBED,
  };
}

export function baseMarketingSubscriptionToConsentStatus(
  subscription: BaseMarketingSubscription | null | undefined,
): MarketingConsentStatus {
  if (!subscription) {
    return MarketingConsentStatus.NOT_SUBSCRIBED;
  }
  if (subscription.unsubscribedDate) {
    return MarketingConsentStatus.UNSUBSCRIBED;
  }
  if (subscription.confirmedDate) {
    return MarketingConsentStatus.CONFIRMED;
  }
  return MarketingConsentStatus.SUBSCRIBED;
}

export type EmailMarketingSubscription = BaseMarketingSubscription & {
  /**
   * Intentionally storing phone number again for redundancy,
   * or in case something goes wrong with the root-level phone number field.
   * This should never be modified.
   */
  email: string;
};

export type SmsMarketingSubscription = BaseMarketingSubscription & {
  phoneNumber: string;
};

export interface CustomerMarketingSubscriptions {
  email?: EmailMarketingSubscription;
  sms?: SmsMarketingSubscription;
}

export interface CustomerMarketingSubscriptionStatuses {
  email?: MarketingConsentStatus;
  sms?: MarketingConsentStatus;
}

// TODO: the customer model should have a string[] field for each identifier,
// uniquely indexed with a collation of strength 2 (case-insensitive)
export const CustomerIdentifiersSchema = z.object({
  redoCustomerId: z.string().optional(),
  shopifyCustomerId: z.string().optional(),
  commentsoldCustomerId: z.string().optional(),
  email: z.string().optional(),
  facebookUserId: z.string().optional(),
  instagramUserId: z.string().optional(),
  tiktokUserId: z.string().optional(),
  postscriptSubscriberId: z.string().optional(),
  anonymousId: z.string().optional(),
  phoneNumber: z
    .string()
    .nullish()
    .transform((val) => val || undefined)
    .optional(), // needed to make key optional for some reason
});
export type CustomerIdentifiers = z.infer<typeof CustomerIdentifiersSchema>;
export type CustomerIdentifier = keyof CustomerIdentifiers;
export interface CustomerIdentifierInfo {
  /**
   * Which identifiers take precedence when identifying a customer.
   *
   * @remarks
   * When choosing the priority of your identifier, ask the following questions about each of the identifiers you send to Segment:
   * 1. Is it an immutable ID? Give immutable IDs, such as user_id, highest priority.
   * 2. Are they unique IDs? Give Unique IDs such as email higher priority than possibly shared identifiers like android.id or ios.id.
   * 3. Does it temporarily identify a user? Identifiers such as anonymous_id, ios.idfa, and ga_client_id are constantly updated or expired for a user. Generally speaking, rank these lower than identifiers that permanently identify a user.
   *
   * @see {@link https://segment.com/docs/unify/identity-resolution/identity-resolution-onboarding/}
   *
   * Each number should be unique.
   */
  priority: number;
  /**
   * How many times a customer can have a specific identifier.
   */
  multiplicity: Multiplicity;
  /**
   * Whether the identifier is unique to a single customer.
   */
  uniqueness: IdentifierUniqueness;
  /**
   * Where the identifiers array is stored in the customer document.
   */
  arrayPath: keyof Customer;
  /**
   * The field name of the identifier field on an object in the identifiers array.
   */
  fieldName: string | null;
  /**
   * How to access the identifier from a customer document.
   */
  accessors: DocumentIdentifierAccessors;
}

export const customerIdentifierPriorities: Record<CustomerIdentifier, number> =
  {
    redoCustomerId: 1,
    shopifyCustomerId: 2,
    commentsoldCustomerId: 3,
    email: 4,
    facebookUserId: 5,
    instagramUserId: 6,
    tiktokUserId: 7,
    postscriptSubscriberId: 8,
    anonymousId: 9,
    phoneNumber: 10,
  };

export enum Multiplicity {
  ZERO_OR_ONE = "zero_or_one",
  EXACTLY_ONE = "exactly_one",
  ZERO_OR_MANY = "zero_or_many",
}

export const multiplicityMaximums: Record<Multiplicity, number> = {
  zero_or_one: 1,
  exactly_one: 1,
  zero_or_many: Infinity,
};

export enum IdentifierUniqueness {
  UNIQUE = "unique",
  POTENTIALLY_SHARED = "potentially_shared",
}

export type PotentiallySharedIdentifiers = Pick<
  CustomerIdentifiers,
  "phoneNumber"
>;

export interface DocumentIdentifierAccessors {
  get: (
    customer: Customer | (Partial<Omit<Customer, "_id">> & { _id: string }),
  ) => { id: string; otherFields?: { [key: string]: any } }[] | undefined;
  set: (
    customer: Customer | Partial<Customer>,
    values: { id: string; otherFields?: { [key: string]: any } }[],
  ) => void;
}

/**
 * An abstraction over all possible identifiers for a customer.
 *
 * It provides various metadata about each identifier, such as:
 * - Priority - Which identifier takes precedence when identifying a customer
 * - Multiplicity - How many times a customer can have a specific identifier
 * - Uniqueness - Whether the identifier is unique to a single customer
 *
 * It also provides a mapping between how identifiers are stored on events vs customers.
 * - Events store a single identifier at root level
 * - Customers store them inside nested arrays
 */
export const customerIdentifierInfo: Record<
  CustomerIdentifier,
  CustomerIdentifierInfo
> = {
  redoCustomerId: {
    priority: 1,
    multiplicity: Multiplicity.EXACTLY_ONE,
    uniqueness: IdentifierUniqueness.UNIQUE,
    arrayPath: "_id",
    fieldName: null,
    accessors: {
      get: (customer) => [{ id: customer._id.toString() }],
      set: (customer, values) => {
        customer._id = values[0].id;
      },
    },
  },
  shopifyCustomerId: {
    priority: 2,
    multiplicity: Multiplicity.ZERO_OR_ONE, // So we can issue store credit to the right account, etc.
    uniqueness: IdentifierUniqueness.UNIQUE,
    arrayPath: "shopifyAccounts",
    fieldName: "id",
    accessors: {
      get: (customer) =>
        customer.shopifyAccounts?.map((account) => ({ id: account.id })),
      set: (customer, values) => {
        if (!customer.shopifyAccounts) {
          customer.shopifyAccounts = [];
        }
        customer.shopifyAccounts = values.map((value) => ({
          ...value.otherFields,
          id: value.id,
        }));
      },
    },
  },
  email: {
    priority: 3,
    multiplicity: Multiplicity.ZERO_OR_ONE,
    uniqueness: IdentifierUniqueness.UNIQUE,
    arrayPath: "emails",
    fieldName: "email",
    accessors: {
      get: (customer) =>
        customer.emails?.map((email) => ({
          id: email.email,
          otherFields: { ...email },
        })),
      set: (customer, values) => {
        if (!customer.emails) {
          customer.emails = [];
        }
        customer.emails = values.map((value) => ({
          ...value.otherFields,
          email: value.id,
        }));
      },
    },
  },
  phoneNumber: {
    priority: 4,
    multiplicity: Multiplicity.ZERO_OR_MANY,
    uniqueness: IdentifierUniqueness.POTENTIALLY_SHARED,
    arrayPath: "phoneNumbers",
    fieldName: "phoneNumber",
    accessors: {
      get: (customer) =>
        customer.phoneNumbers?.map((phoneNumber) => ({
          id: phoneNumber.phoneNumber,
          otherFields: { ...phoneNumber },
        })),
      set: (customer, values) => {
        if (!customer.phoneNumbers) {
          customer.phoneNumbers = [];
        }
        customer.phoneNumbers = values.map((value) => ({
          ...value.otherFields,
          phoneNumber: value.id,
        }));
      },
    },
  },
  commentsoldCustomerId: {
    priority: 5,
    multiplicity: Multiplicity.ZERO_OR_MANY,
    uniqueness: IdentifierUniqueness.POTENTIALLY_SHARED,
    arrayPath: "commentsoldAccounts",
    fieldName: "id",
    accessors: {
      get: (customer) =>
        customer.commentsoldAccounts?.map((account) => ({ id: account.id })),
      set: (customer, values) => {
        if (!customer.commentsoldAccounts) {
          customer.commentsoldAccounts = [];
        }
        customer.commentsoldAccounts = values.map((value) => ({
          ...value.otherFields,
          id: value.id,
          commentsoldUpdatedAt: value.otherFields?.commentsoldUpdatedAt ?? null,
        }));
      },
    },
  },
  facebookUserId: {
    priority: 6,
    multiplicity: Multiplicity.ZERO_OR_MANY,
    uniqueness: IdentifierUniqueness.UNIQUE,
    arrayPath: "facebookAccounts",
    fieldName: "pageScopedId",
    accessors: {
      get: (customer) =>
        customer.facebookAccounts?.map((account) => ({
          id: account.pageScopedId,
          otherFields: {
            name: account.name,
            firstName: account.firstName,
            lastName: account.lastName,
            profilePic: account.profilePic,
          },
        })),
      set: (customer, values) => {
        if (!customer.facebookAccounts) {
          customer.facebookAccounts = [];
        }
        customer.facebookAccounts = values.map((value) => ({
          ...value.otherFields,
          pageScopedId: value.id,
        }));
      },
    },
  },
  instagramUserId: {
    priority: 7,
    multiplicity: Multiplicity.ZERO_OR_MANY,
    uniqueness: IdentifierUniqueness.UNIQUE,
    arrayPath: "instagramAccounts",
    fieldName: "igScopedUserId",
    accessors: {
      get: (customer) =>
        customer.instagramAccounts?.map((account) => ({
          id: account.igScopedUserId,
          otherFields: {
            username: account.username,
            name: account.name,
            profilePic: account.profilePic,
          },
        })),
      set: (customer, values) => {
        if (!customer.instagramAccounts) {
          customer.instagramAccounts = [];
        }
        customer.instagramAccounts = values.map((value) => ({
          ...value.otherFields,
          igScopedUserId: value.id,
        }));
      },
    },
  },
  tiktokUserId: {
    priority: 8,
    multiplicity: Multiplicity.ZERO_OR_MANY,
    uniqueness: IdentifierUniqueness.UNIQUE,
    arrayPath: "tiktokAccounts",
    fieldName: "userId",
    accessors: {
      get: (customer) =>
        customer.tiktokAccounts?.map((account) => ({
          id: account.userId,
          otherFields: { name: account.name, profilePic: account.profilePic },
        })),
      set: (customer, values) => {
        if (!customer.tiktokAccounts) {
          customer.tiktokAccounts = [];
        }
        customer.tiktokAccounts = values.map((value) => ({
          ...value.otherFields,
          userId: value.id,
        }));
      },
    },
  },
  postscriptSubscriberId: {
    priority: 9,
    multiplicity: Multiplicity.ZERO_OR_MANY,
    uniqueness: IdentifierUniqueness.UNIQUE,
    arrayPath: "postscriptSubscriptions",
    fieldName: "subscriberId",
    accessors: {
      get: (customer) =>
        customer.postscriptSubscriptions?.map((subscription) => ({
          id: subscription.subscriberId,
        })),
      set: (customer, values) => {
        if (!customer.postscriptSubscriptions) {
          customer.postscriptSubscriptions = [];
        }
        customer.postscriptSubscriptions = values.map((value) => ({
          ...value.otherFields,
          subscriberId: value.id,
        }));
      },
    },
  },
  anonymousId: {
    priority: 10,
    multiplicity: Multiplicity.ZERO_OR_MANY,
    uniqueness: IdentifierUniqueness.UNIQUE,
    arrayPath: "anonymousIds",
    fieldName: "id",
    accessors: {
      get: (customer) =>
        customer.anonymousIds?.map((anonymousId) => ({ id: anonymousId.id })),
      set: (customer, values) => {
        if (!customer.anonymousIds) {
          customer.anonymousIds = [];
        }
        customer.anonymousIds = values.map((value) => ({ id: value.id }));
      },
    },
  },
};

interface CustomerLike {
  name: string;
  firstName: string;
  lastName: string;
  instagramAccounts: NullishPartial<{
    username: string;
    name: string;
    profilePic: string;
    igScopedUserId: string;
  }>[];
  facebookAccounts: NullishPartial<{
    name: string;
    profilePic: string;
    firstName: string;
    lastName: string;
    pageScopedId: string;
  }>[];
  tiktokAccounts: NullishPartial<{
    name: string;
    profilePic: string;
    userId: string;
  }>[];
  phoneNumbers: { phoneNumber: string }[];
  emails: { email: string }[];
  shopifyAccounts: { id: string }[];
  addresses: any[];
  postscriptSubscriptions: { subscriberId: string }[];
}

export const DEFAULT_CUSTOMER_NAME = "Unknown Customer";

export function getCustomerDisplayName(
  customer: NullishPartial<CustomerLike> | null | undefined,
): string {
  if (!customer) {
    return DEFAULT_CUSTOMER_NAME;
  }
  if (customer.name) {
    return customer.name;
  }
  if (customer.firstName && customer.lastName) {
    return `${customer.firstName} ${customer.lastName}`;
  }
  if (customer.firstName) {
    return customer.firstName;
  }
  const instagramAccount = customer.instagramAccounts?.[0];
  if (instagramAccount) {
    if (instagramAccount.username) {
      return `@${instagramAccount.username}`;
    } else if (instagramAccount.name) {
      return instagramAccount.name;
    }
  }
  const facebookAccount = customer.facebookAccounts?.[0];
  if (facebookAccount && facebookAccount.name) {
    return facebookAccount.name;
  }
  const tiktokAccount = customer.tiktokAccounts?.[0];
  if (tiktokAccount && tiktokAccount.name) {
    return tiktokAccount.name;
  }
  const phoneNumber = customer.phoneNumbers?.[0];
  if (phoneNumber) {
    return phoneNumber.phoneNumber;
  }
  const email = customer.emails?.[0];
  if (email) {
    return email.email;
  }
  return DEFAULT_CUSTOMER_NAME;
}

export function getPrimaryCustomerPostscriptSubscriberId(
  customer: NullishPartial<CustomerLike> | undefined | null,
): string | undefined {
  return customer?.postscriptSubscriptions?.[0]?.subscriberId;
}

export function getPrimaryCustomerEmail(
  customer: NullishPartial<CustomerLike> | undefined | null,
): string | undefined {
  return customer?.emails?.[0]?.email;
}

export function getPrimaryCustomerPhoneNumber(
  customer: NullishPartial<CustomerLike> | undefined | null,
): string | undefined {
  return customer?.phoneNumbers?.[0]?.phoneNumber;
}

/**
 * Gets the most appropriate email for marketing communications, if any.
 */
export function getPrimaryMarketingEmail(customer: Pick<Customer, "emails">) {
  return (
    customer.emails?.find(
      (e) =>
        baseMarketingSubscriptionToConsentStatus(e.marketingSubscription) ===
        MarketingConsentStatus.CONFIRMED,
    ) ||
    customer.emails?.find(
      (e) =>
        baseMarketingSubscriptionToConsentStatus(e.marketingSubscription) ===
        MarketingConsentStatus.SUBSCRIBED,
    ) ||
    customer.emails?.find(
      (e) =>
        baseMarketingSubscriptionToConsentStatus(e.marketingSubscription) ===
        MarketingConsentStatus.NOT_SUBSCRIBED,
    ) ||
    customer.emails?.[0]
  );
}

/**
 * Gets the most appropriate phone number for marketing communications, if any
 */
export function getPrimaryMarketingPhoneNumber(
  customer: Pick<Customer, "phoneNumbers">,
) {
  // For SMS sending, a customer must be explicitly opted in.
  return (
    customer.phoneNumbers?.find(
      (p) =>
        baseMarketingSubscriptionToConsentStatus(p.marketingSubscription) ===
        MarketingConsentStatus.CONFIRMED,
    ) ||
    customer.phoneNumbers?.find(
      (p) =>
        baseMarketingSubscriptionToConsentStatus(p.marketingSubscription) ===
        MarketingConsentStatus.SUBSCRIBED,
    ) ||
    customer.phoneNumbers?.find(
      (p) =>
        baseMarketingSubscriptionToConsentStatus(p.marketingSubscription) ===
        MarketingConsentStatus.NOT_SUBSCRIBED,
    ) ||
    customer.phoneNumbers?.[0]
  );
}

export function getPrimaryCustomerAddress(
  customer: NullishPartial<CustomerLike> | undefined | null,
): NullishPartial<CustomerAddress> | undefined {
  return customer?.addresses?.[0] || undefined;
}

export function getPrimaryCustomerFacebookName(
  customer: NullishPartial<CustomerLike> | undefined | null,
): string | undefined {
  return customer?.facebookAccounts?.[0]?.name || undefined;
}

export function getPrimaryCustomerInstagram(
  customer: NullishPartial<CustomerLike> | undefined | null,
): CustomerLike["instagramAccounts"][number] | undefined {
  return customer?.instagramAccounts?.[0] || undefined;
}

export function getPrimaryCustomerFacebook(
  customer: NullishPartial<CustomerLike> | undefined | null,
): CustomerLike["facebookAccounts"][number] | undefined {
  return customer?.facebookAccounts?.[0] || undefined;
}

export function getPrimaryCustomerTiktok(
  customer: NullishPartial<CustomerLike> | undefined | null,
): CustomerLike["tiktokAccounts"][number] | undefined {
  return customer?.tiktokAccounts?.[0] || undefined;
}

export function getPrimaryCustomerInstagramUsername(
  customer: NullishPartial<CustomerLike> | undefined | null,
): string | undefined {
  return customer?.instagramAccounts?.[0]?.username || undefined;
}

export function getPrimaryCustomerShopifyCustomerId(
  customer: NullishPartial<CustomerLike> | undefined | null,
): `${number}` | undefined {
  const idOpt = customer?.shopifyAccounts?.[0]?.id;
  if (idOpt) {
    return parseNumericIdFromShopifyId(idOpt);
  }
  return undefined;
}

export interface SimplifiedCustomer {
  email: string | undefined | null;
  phone: string | undefined | null;
  first_name: string | undefined | null;
  last_name: string | undefined | null;
  supportCommunicationConsent?: {
    textMessages?: { optInDate?: Date | null; optOutDate?: Date | null } | null;
  } | null;
  default_address?: any;
}

// ===== Zod schema =====

const imageSchema = z.object({ url: z.string().optional() });
export type CustomerProfileImage = z.infer<typeof imageSchema>;

const marketingSubscriptionSchema = z.object({
  entryPoint: createEnum(MarketingEntryPoint, {
    enumName: "MarketingEntryPoint",
  }).optional(),
  // TODO: Once we remove discount_code from the schema for marketing signups, we can remove this field.
  formId: zExt.objectId().optional(),
  subscribedDate: z.date().optional(),
  confirmedDate: z.date().optional(),
  unsubscribedDate: z.date().optional(),
});
type BaseMarketingSubscriptionNoTsDoc = z.infer<
  typeof marketingSubscriptionSchema
>;
/**
 * Represents an individual marketing subscription for a customer.
 * Most of the fields are collected due to guidelines from the T-Mobile code of conduct:
 * https://www.t-mobile.com/support/public-files/attachments/T-Mobile%20Code%20of%20Conduct.pdf
 */
export type BaseMarketingSubscription = BaseMarketingSubscriptionNoTsDoc & {
  /**
   * Currently only applies to SMS subscriptions.
   * But just in case we need to add this to email subscriptions in the future.
   */
  confirmedDate?: BaseMarketingSubscriptionNoTsDoc["confirmedDate"];
  /**
   * The date the customer unsubscribed from marketing.
   * At most one of confirmedDate or unsubscribedDate should be set.
   */
  unsubscribedDate?: BaseMarketingSubscriptionNoTsDoc["unsubscribedDate"];
};

const recoverSubscription = z.object({ unsubscribedAt: z.date().optional() });
export type RecoverSubscription = z.infer<typeof recoverSubscription>;

const supportCommunicationConsentSchema = z.object({
  textMessages: z
    .object({ optInDate: z.date().nullish(), optOutDate: z.date().nullish() })
    .nullish()
    .transform((value) => value || undefined)
    .optional(),
});
export type CustomerSupportCommunicationConsent = z.infer<
  typeof supportCommunicationConsentSchema
>;

const emailInfoSchema = z.object({
  email: z.string(),
  marketingSubscription: marketingSubscriptionSchema.nullish(),
});

const phoneNumberInfoSchema = z.object({
  phoneNumber: z.string(),
  supportCommunicationConsent: supportCommunicationConsentSchema.nullish(),
  marketingSubscription: marketingSubscriptionSchema.nullish(),
  recoverSubscription: recoverSubscription.nullish(),
});

const baseAccountSchema = z.object({ id: z.string() });
const shopifyAccountSchema = baseAccountSchema;
const commentsoldAccountSchema = baseAccountSchema.extend({
  commentsoldUpdatedAt: zExt.coerceNull(z.date()),
});

const instagramSchema = z.object({
  igScopedUserId: z.string(),
  username: z.string().nullish(),
  name: z.string().nullish(),
  profilePic: z.string().nullish(),
});

const facebookSchema = z.object({
  pageScopedId: z.string(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  profilePic: z.string().nullish(),
});

const tiktokSchema = z.object({
  userId: z.string(),
  name: z.string().nullish(),
  profilePic: z.string().nullish(),
});

const postscriptSchema = z.object({ subscriberId: z.string() });

export type EmailInfo = z.infer<typeof emailInfoSchema>;
export type PhoneNumberInfo = z.infer<typeof phoneNumberInfoSchema>;
export type ChannelRecord = EmailInfo | PhoneNumberInfo;

export interface CustomerTagWithId {
  tagId: string;
  name: string;
  pillTheme: PillTheme;
  source: CustomerTagSource;
}
const customerTagWithIdSchema = z.object({
  tagId: zExt.objectId(),
  name: z.string(),
  pillTheme: createEnum(PillTheme, { enumName: "PillTheme" }),
  source: createEnum(CustomerTagSource, { enumName: "CustomerTagSource" }),
});

// TODO: Migrate this to match the address schema from "@redotech/redo-model/address
export const addressSchema = z.object({
  name: z.string().optional(),
  street1: z.string().optional(),
  street2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  country: z.string().optional(),
  country_name: z.string().optional(),
  phone: z.string().optional(),
});

const currentLocationSchema = z.object({
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  postalCode: z.string().nullish(),
  country: z.string().nullish(),

  countryCode: z.string().nullish(),
  stateCode: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  geoLocation: z
    .object({
      type: z.literal("Point"),
      coordinates: z.tuple([z.number(), z.number()]),
    })
    .nullish(),
  ianaTimeZoneName: z.string().nullish(),
  source: createEnum(LocationSource, { enumName: "LocationSource" }),
  updatedAt: z.date(),
  updatedByEventTimestamp: z.date().nullish(),
});

const customerInsightFieldSchema = z.object({
  insight: z.string(), // e.g., "Shops mostly late at night on mobile; responds well to flash sales"
  confidence: z.number().min(1).max(100), // Confidence in the insight
});

export const customerInsightsSchema = z.object({
  shoppingBehavior: customerInsightFieldSchema.optional(),
  customerType: customerInsightFieldSchema.optional(),
  valueDrivers: customerInsightFieldSchema.optional(),
  nextPurchaseTiming: customerInsightFieldSchema.optional(),
});

export type CustomerInsights = z.infer<typeof customerInsightsSchema>;

const customerProfileSchema = z.object({
  /**
   * AI Generated Bio for the customer.
   */
  bio: z.string(),
  /**
   * Reasoning behind the AI generated bio.
   * Should contain information like how the values were estimated, what data was used, etc.
   */
  bioReasoning: z.string(),
  /**
   * Confidence score for the AI generated profile (1-100).
   * Indicates how confident the AI is in the accuracy of the generated profile.
   */
  confidence: z.number().min(1).max(100).optional(),
  /**
   * Timestamp when the AI generated profile was created.
   */
  generatedAt: z.date().optional(),
  /**
   * Analysis fields
   */

  insights: customerInsightsSchema.optional(),
});

export type CustomerProfile = z.infer<typeof customerProfileSchema>;

export const loyaltyCompletedEarningRuleBaseSchema = z.object({
  type: createEnum(LoyaltyEarningRuleType, {
    enumName: "LoyaltyEarningRuleType",
  }),
  completedAt: z.date(),
});

export const loyaltyCompletedPurchaseRuleSchema =
  loyaltyCompletedEarningRuleBaseSchema.extend({
    type: z.literal(LoyaltyEarningRuleType.PURCHASE),
  });

export const loyaltyCompletedReviewRuleSchema =
  loyaltyCompletedEarningRuleBaseSchema.extend({
    type: z.literal(LoyaltyEarningRuleType.REVIEW),
    reviewId: zExt.objectId(),
  });

export const loyaltyCompletedBirthdayRuleSchema =
  loyaltyCompletedEarningRuleBaseSchema.extend({
    type: z.literal(LoyaltyEarningRuleType.BIRTHDAY),
    lastBirthdayYearEarned: z.number(),
  });

export const loyaltyCompletedAccountCreationRuleSchema =
  loyaltyCompletedEarningRuleBaseSchema.extend({
    type: z.literal(LoyaltyEarningRuleType.ACCOUNT_CREATION),
  });

export const loyaltyCompletedInstagramRuleSchema =
  loyaltyCompletedEarningRuleBaseSchema.extend({
    type: z.literal(LoyaltyEarningRuleType.INSTAGRAM),
  });

export const loyaltyCompletedFacebookRuleSchema =
  loyaltyCompletedEarningRuleBaseSchema.extend({
    type: z.literal(LoyaltyEarningRuleType.FACEBOOK),
  });

export const loyaltyCompletedTiktokRuleSchema =
  loyaltyCompletedEarningRuleBaseSchema.extend({
    type: z.literal(LoyaltyEarningRuleType.TIKTOK),
  });

export type LoyaltyCompletedInstagramRule = z.infer<
  typeof loyaltyCompletedInstagramRuleSchema
>;

export type LoyaltyCompletedFacebookRule = z.infer<
  typeof loyaltyCompletedFacebookRuleSchema
>;

export type LoyaltyCompletedTiktokRule = z.infer<
  typeof loyaltyCompletedTiktokRuleSchema
>;

export type LoyaltyCompletedAccountCreationRule = z.infer<
  typeof loyaltyCompletedAccountCreationRuleSchema
>;

export type LoyaltyCompletedEarningRule = z.infer<
  typeof loyaltyCompletedEarningRuleBaseSchema
>;

export type LoyaltyCompletedPurchaseRule = z.infer<
  typeof loyaltyCompletedPurchaseRuleSchema
>;

export type LoyaltyCompletedReviewRule = z.infer<
  typeof loyaltyCompletedReviewRuleSchema
>;

export type LoyaltyCompletedBirthdayRule = z.infer<
  typeof loyaltyCompletedBirthdayRuleSchema
>;

const loyaltySchema = z.object({
  currentPoints: z.number(),
  lifetimePointsEarned: z.number(),
  lifetimePointsRedeemed: z.number(),
  lifetimeValueRedeemed: z.string(),
  completedEarningRules: z.array(loyaltyCompletedEarningRuleBaseSchema),
  /**
   * @deprecated Use completedEarningRules instead
   */
  socialMediaInteractions: z.array(LoyaltySocialMediaInteractionTypeSchema),
  /**
   * @deprecated Use completedEarningRules instead
   */
  lastBirthdayYearEarned: z.number(),
  enrolledDate: z.date(),
  activeDiscountCodeId: z.string().nullish(), // Tracks the discount code that is currently active for the customer.
});

export const CustomerSchema_ = z.object({
  _id: zExt.objectId(),
  firstName: z
    .string()
    .nullish()
    .transform((name) => name || undefined)
    .optional(),
  lastName: z
    .string()
    .nullish()
    .transform((name) => name || undefined)
    .optional(),
  name: z
    .string()
    .nullish()
    .transform((name) => name || undefined)
    .optional(),
  image: imageSchema.nullish(),

  team: zExt.objectId(),
  origin: createEnum(CustomerOrigin, { enumName: "CustomerOrigin" }).nullish(),

  supportCommunicationConsent: supportCommunicationConsentSchema.nullish(),
  marketingSubscriptionStatuses: z
    .object({
      email: createEnum(MarketingConsentStatus, {
        enumName: "MarketingConsentStatus",
      }).optional(),
      sms: createEnum(MarketingConsentStatus, {
        enumName: "MarketingConsentStatus",
      }).optional(),
    })
    .nullish(),

  emails: z.array(emailInfoSchema).nullish(),
  phoneNumbers: z.array(phoneNumberInfoSchema).nullish(),
  shopifyAccounts: z.array(shopifyAccountSchema).nullish(),
  commentsoldAccounts: z.array(commentsoldAccountSchema).nullish(),
  instagramAccounts: z.array(instagramSchema).nullish(),
  facebookAccounts: z.array(facebookSchema).nullish(),
  tiktokAccounts: z.array(tiktokSchema).nullish(),
  postscriptSubscriptions: z.array(postscriptSchema).nullish(),
  anonymousIds: z.array(z.object({ id: z.string() })).nullish(),

  yofiScores: z
    .record(z.string(), YofiScoreSchema)
    .nullish()
    .transform((value) => value || undefined)
    .optional(),

  tags: z.array(z.string()).nullish(),
  tagIds: z.array(customerTagWithIdSchema).nullish(),
  addresses: z.array(addressSchema).nullish(),
  currentLocation: currentLocationSchema
    .nullish()
    .transform((value) => value || undefined)
    .optional(),
  birthday: z.date().nullish(),
  customerGroupMemberships: z
    .array(
      z.object({
        customerGroupId: zExt.objectId(),
        addedAt: z.date(),
        // Optional fields for persona assignments
        reasoning: z.string().optional(),
        confidence: z.number().min(1).max(100).optional(),
        updatedAt: z.date().nullish(),
      }),
    )
    .nullish(),

  customFields: z.array(customerCustomFieldInstanceSchema).nullish(),

  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),

  profile: customerProfileSchema.nullish(),

  loyalty: loyaltySchema.nullish(),
});
export const CustomerSchema: z.ZodType<Customer> = CustomerSchema_;
interface Customer_ extends z.infer<typeof CustomerSchema_> {}
export interface Customer extends Customer_ {
  /**  @deprecated - this is now stored per-email and per-phone */
  supportCommunicationConsent?: Customer_["supportCommunicationConsent"];
  /** @deprecated - this is now stored per-email and per-phone */
  marketingSubscriptionStatuses?: Customer_["marketingSubscriptionStatuses"];
}
